/**
 * @file PdfViewer — renders a PDF (single or multi-page) to canvas via pdf.js.
 *
 * Why this exists (vs an <iframe>):
 *   - iOS Safari and macOS Safari refuse to render PDFs inside iframes.
 *     <iframe src="*.pdf"> shows a download prompt instead of the document.
 *   - Chrome's built-in PDF viewer injects its own toolbar and sidebar inside
 *     the iframe — visual noise our designers can't style away.
 *   - We need consistent rendering and our own UI shell across all browsers.
 *
 * Scaling strategy:
 *   - Read REAL page dimensions from pdf.js (`page.getViewport({scale:1})`)
 *     rather than hardcoding A4 landscape. Works for portrait, square, A3, etc.
 *   - Observe the container element (not `window`) via ResizeObserver, so the
 *     PDF fills the modal's actual content area regardless of window size.
 *   - `scale = min(containerW / pageW, containerH / pageH) * SAFETY` gives a
 *     true "contain" fit that re-runs on resize and on page change.
 *
 * Bundle impact: imported lazily by ResourcesPane → ~430 kB in a separate
 * chunk, fetched only when the user first opens a PDF preview.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

// Vite's `?url` suffix returns the asset's emitted URL. This is the supported
// way to bundle the pdf.js worker — it gets hashed, served from /assets/, and
// resolves the bare module specifier that `new URL(..., import.meta.url)` can't.
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

import { useI18n } from '../../i18n/useI18n.js'

import { ArrowLeft } from '../icons/ArrowLeft.jsx'
import { Button } from '../Button/index.js'
import { Loading } from '../Loading.jsx'
import styles from './PdfViewer.module.scss'

// ── pdf.js worker setup ────────────────────────────────────────────────────────
pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl

// Leave a small margin so the page never touches the modal edges.
const FIT_SAFETY = 0.98

/**
 * @param {object} props
 * @param {string} props.file       - URL (blob: or http:) of the PDF to render
 * @param {string} [props.ariaLabel] - Accessible label for the canvas region
 */
export function PdfViewer({ file, ariaLabel }) {
  const containerRef       = useRef(null)
  const pageNativeSizeRef  = useRef(null)  // {width,height} of current page at scale 1

  const { t } = useI18n();

  const [numPages,   setNumPages]   = useState(1)
  const [pageNumber, setPageNumber] = useState(1)
  const [scale,      setScale]      = useState(1)
  const [loadError,  setLoadError]  = useState(null)

  // ── Scale recalculation ──────────────────────────────────────────────────────
  // Called when: container resizes, page changes, or a new page's native size loads.
  const recomputeScale = useCallback(() => {
    const container = containerRef.current
    const native    = pageNativeSizeRef.current
    if (!container || !native) return

    const { clientWidth: cw, clientHeight: ch } = container
    if (cw <= 0 || ch <= 0) return

    const fit = Math.min(cw / native.width, ch / native.height) * FIT_SAFETY
    setScale(fit > 0 ? fit : 1)
  }, [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const ro = new ResizeObserver(recomputeScale)
    ro.observe(el)
    return () => ro.disconnect()
  }, [recomputeScale])

  // ── Document & page callbacks ────────────────────────────────────────────────
  const handleDocumentLoad = useCallback(({ numPages }) => {
    setNumPages(numPages)
    setPageNumber(1)
    setLoadError(null)
  }, [])

  const handleDocumentError = useCallback((err) => {
    setLoadError(err?.message || 'Failed to load PDF')
  }, [])

  // Called for every <Page> render; pdf.js gives us the page proxy here.
  // We capture native size (scale=1 viewport) and trigger a fit recompute.
  const handlePageLoad = useCallback((page) => {
    const viewport = page.getViewport({ scale: 1 })
    pageNativeSizeRef.current = { width: viewport.width, height: viewport.height }
    recomputeScale()
  }, [recomputeScale])

  const goPrev = useCallback(() => setPageNumber((p) => Math.max(p - 1, 1)),         [])
  const goNext = useCallback(() => setPageNumber((p) => Math.min(p + 1, numPages)), [numPages])

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className={styles.viewer} aria-label={ariaLabel}>
      <div ref={containerRef} className={styles.canvasArea}>
        {loadError ? (
          <div className={styles.error} role="alert">
            {t('product.resource_pdf_load_error')}
            {/* {loadError}
            
            <p style={{ marginBottom: '1rem' }}>
              Nie udało się załadować podglądu pliku (możliwa blokada CORS).
            </p> */}
            <Button
              type="button"
              kind="text"
              size="large"
              // Dopasuj prop `kind` i `variant` do Twojego systemu designu, np. 'primary' czy 'solid'
              onClick={() => window.open(file, '_blank', 'noopener,noreferrer')}
            >
              {t('product.resource_pdf_open_new_window')}
            </Button>
            
            </div>
        ) : (
          <Document
            file={file}
            onLoadSuccess={handleDocumentLoad}
            onLoadError={handleDocumentError}
            loading={<Loading />}
            error={null}
          >
            <Page
              pageNumber={pageNumber}
              scale={scale}
              onLoadSuccess={handlePageLoad}
              renderTextLayer={false}
              renderAnnotationLayer={false}
              loading={<Loading />}
            />
          </Document>
        )}
      </div>

      {numPages > 1 && (
        <div className={styles.pager}>

              {/* <ButtonGroup
                size="small"
                className={styles.pageNavGroup}
                aria-label={t('product.navigation_aria')}
                tabIndex={navigationDisabledReason ? 0 : undefined}
              > */}
                <Button
                  type="button"
                  kind="icon"
                  size="small"
                  // className={styles.pageNavButton}
            variant="ghost"
            onClick={goPrev}
            disabled={pageNumber === 1}
            aria-label="Previous page"
                >
                  <ArrowLeft 
                  // width="0.7em" height="1em" 
                  />
                </Button>
                          <span className={styles.pagerLabel}>
            {pageNumber} / {numPages}
          </span>
                <Button
                  type="button"
                  kind="icon"
                  size="small"
                  // className={styles.pageNavButton}
            variant="ghost"
            onClick={goNext}
            disabled={pageNumber === numPages}
            aria-label="Next page"
                >
                  <ArrowLeft className={styles.pageNavIconNext} 
                  // width="0.7em" height="1em" 
                  />
                </Button>
              {/* </ButtonGroup> */}


          {/* <Button
            type="button"
            kind="icon"
            size="small"
            variant="ghost"
            onClick={goPrev}
            disabled={pageNumber === 1}
            aria-label="Previous page"
          >
            ◀
          </Button>
          <span className={styles.pagerLabel}>
            {pageNumber} / {numPages}
          </span>
          <Button
            type="button"
            kind="icon"
            size="small"
            variant="ghost"
            onClick={goNext}
            disabled={pageNumber === numPages}
            aria-label="Next page"
          >
            ▶
          </Button> */}
        </div>
      )}
    </div>
  )
}

export default PdfViewer
